import { Badge } from '@/components/ui/badge';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { PlatformBadge } from '@/components/shared/platform/PlatformBadge';
import type {
  GroupedAlert,
  AlertOccurrence,
  Outcome,
} from '@kirin/api-client/contracts/alerts';
import { getAlertFileDisplayLabel } from '@kirin/api-client/contracts';
import type { ToolEventKind } from '@kirin/api-client/contracts';
import {
  formatAlertType,
  formatSubPolicyLabel,
} from '@kirin/api-client/helpers/alerts';
import { MiddleTruncatedPath } from '@/components/shared/MiddleTruncatedPath';
import { ShieldCheck, Eye, HelpCircle } from 'lucide-react';

export function FilePathCellValue({
  filePath,
  location,
  eventKind,
  className,
}: {
  filePath?: string | null;
  /** The alert/occurrence `location` — the real file path when one is known. */
  location?: string | null;
  /** KIR-3765: first-class tool-event side, drives the Tool Input/Output label. */
  eventKind?: ToolEventKind | null;
  className?: string;
}) {
  if (!filePath && !location) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  const label = getAlertFileDisplayLabel({ filePath, location, eventKind });
  // KIR-2563: width-measured middle truncation keeps the filename visible and
  // the full path copyable via a dedicated affordance. Only that affordance
  // stops propagation, so clicking the cell still triggers the row action.
  // KIR-3810: alert File cells also get a one-click copy button.
  return (
    <MiddleTruncatedPath path={label} className={className} showInlineCopy />
  );
}

// KIR-2941: shared column width for the Title column rendered by TypeCell.
// Sized to fit today's longest registry title ("AI assistant mode with
// auto-apply enabled", 41 chars → ~274px badge) plus 24px cell padding,
// with a 2px buffer. Both org and personal alerts tables import this so
// they stay in lockstep.
export const TITLE_COLUMN_WIDTH = 300;

export function TypeCell({
  row,
  revampEnabled = false,
}: {
  row: GroupedAlert;
  // KIR-2381: threaded from the column factory so the flag is read once,
  // not per row. Default `false` keeps the legacy off-state byte-identical.
  revampEnabled?: boolean;
}) {
  const label = revampEnabled
    ? formatSubPolicyLabel(row.subPolicyKey, row.type)
    : formatAlertType(row.type);
  // KIR-2941: Badge defaults to `whitespace-nowrap shrink-0 w-fit`, which lets
  // a title overflow its column and crash into the Severity badge. Cap the
  // badge at the cell width and allow wrapping as a fallback — the column is
  // wide enough for today's longest title (41 chars), but this keeps us safe
  // for new or translated titles that grow past that.
  return (
    <Badge
      variant="secondary"
      className="max-w-full whitespace-normal text-left"
    >
      {label}
    </Badge>
  );
}

export function SeverityCell({ row }: { row: GroupedAlert }) {
  return <SeverityBadge riskLevel={row.severity} showIcon />;
}

export function FilePathCell({ row }: { row: GroupedAlert }) {
  return (
    <FilePathCellValue
      filePath={row.filePath}
      location={row.location}
      eventKind={row.eventKind}
    />
  );
}

export function IssuesCell({ row }: { row: GroupedAlert }) {
  return <span className="text-sm">{row.occurrenceCount ?? '—'}</span>;
}

export function ScoreCell({ row }: { row: GroupedAlert }) {
  return <span className="text-sm">{row.score ?? '—'}</span>;
}

export function LastSeenCell({ row }: { row: GroupedAlert }) {
  return <RelativeTimeWithTooltip date={row.lastSeenAt} showIcon={false} />;
}

export function FirstSeenCell({ row }: { row: GroupedAlert }) {
  return <RelativeTimeWithTooltip date={row.firstSeenAt} showIcon={false} />;
}

export function ClientTypesCell({ row }: { row: GroupedAlert }) {
  const types = row.clientTypes ?? [];
  if (types.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {types.map(type => (
        <PlatformBadge key={type} platform={type} showTooltip={false} />
      ))}
    </div>
  );
}

export function ClientTypeCell({ value }: { value?: string | null }) {
  if (!value) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return <PlatformBadge platform={value} showTooltip={false} />;
}

const OUTCOME_CONFIG: Record<
  string,
  {
    label: string;
    variant: 'default' | 'secondary' | 'outline';
    icon: typeof ShieldCheck;
  }
> = {
  prevented: { label: 'Prevented', variant: 'default', icon: ShieldCheck },
  detected: { label: 'Detected', variant: 'secondary', icon: Eye },
  unknown: { label: 'Unknown', variant: 'outline', icon: HelpCircle },
};

export function OutcomeBadge({
  outcome,
}: {
  outcome?: Outcome | string | null;
}) {
  const config = OUTCOME_CONFIG[outcome ?? 'unknown'] ?? OUTCOME_CONFIG.unknown;
  const Icon = config.icon;
  return (
    <Badge
      variant={config.variant}
      className={
        outcome === 'prevented'
          ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
          : outcome === 'detected'
            ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
            : ''
      }
    >
      <Icon className="size-3.5" />
      {config.label}
    </Badge>
  );
}

export function OutcomeCell({ row }: { row: GroupedAlert }) {
  return <OutcomeBadge outcome={row.outcome} />;
}

export function OccurrenceOutcomeCell({ row }: { row: AlertOccurrence }) {
  const outcome = row.outcome ?? 'unknown';
  return <OutcomeBadge outcome={outcome} />;
}
